import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';

import { type AIActionType, useSourceAIActions } from '@atlassian/ai-smart-button-api';

import { FooterCommentsSummaryCombined } from '@confluence/ai-comments-summary/entry-points/FooterCommentsSummaryCombined.ts';
import { CommentsSummarySource } from '@confluence/ai-comments-summary/entry-points/CommentsSummaryContent.ts';
import { useAutomationPermissionService } from '@confluence/automation-menu/entry-points/AutomationPermissionService.ts';
import {
	CommentCountsContext,
	CommentCountsProvider,
} from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import {
	type ConversationAction,
	HandleActionType,
} from '@confluence/conversation-assistant/entry-points/conversation-assistant.ts';
import { ExploreRelatedWork } from '@confluence/explore-related-work';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	useWarningDialogStateActions,
	WarningDialog,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateWarningDialogContextProvider.ts';
import { BULK_ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';
import { PageCatchupComponent } from '@confluence/page-catchup/entry-points/PageCatchupStreamingEntryPoint.tsx';
import { FilterComponent } from '@confluence/page-catchup/entry-points/FilterComponent.tsx';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { QuickSummaryStreamingComponent } from '@confluence/quick-summary/entry-points/QuickSummaryStreamingEntryPoint.ts';
import { useAIActions } from '@confluence/ai-rovo-button/entry-points/useAIActions.tsx';

import { useBulkIssueCreate } from './useBulkIssueCreate.ts';
import { useDynamicSplitButton } from './useDynamicSplitButton.ts';
import { useNudgeRecommendationFilter } from './useNudgeRecommendationFilter.tsx';

export const i18n = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	summarizeSectionLabel: {
		id: 'ai-floating-context-menu.summarize.section.label',
		defaultMessage: 'Summarize page',
		description: 'Label for the summarize section in the menu group',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
	exploreRelatedWorkAndUpdates: {
		id: 'ai-floating-context-menu.menu.item.label.explore-related-items.non-final',
		defaultMessage: 'Explore related work and resources',
		description: 'Label for the Explore Related Items Menu Item',
	},
});

const VIEW_PAGE_SOURCE_ID = 'view-page';

type AIConsumptionViewPageProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	contentTitle: string;
	lastModifiedDate?: string | null;
	isUnpublishedDraft?: boolean;
};

export const useAIConsumptionViewPage = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	lastModifiedDate,
	isUnpublishedDraft,
}: AIConsumptionViewPageProps) => {
	const { formatMessage } = useIntl();
	const { setNudgesRefreshed } = useSourceAIActions(VIEW_PAGE_SOURCE_ID);
	const { setConsumptionActions } = useAIActions();
	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const pageMode = useGetPageMode();
	const { canManageAutomations } = useAutomationPermissionService(spaceKey);
	const enableNudgeCheck = useNudgeRecommendationFilter(contentId, contentType);

	const rightSidebarContext = useRightSidebarContext();
	const { showWarningDialog } = useWarningDialogStateActions();
	const contentTypeEnum = contentType === 'page' ? 'PAGE' : 'BLOGPOST';
	const { showCatchupAsDefault, showSummarizeAsDefault } = useDynamicSplitButton(
		contentId,
		contentTypeEnum,
		true,
	);
	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
	});

	const pageCatchupAction = useMemo(
		() => ({
			id: 'summarize-changes-ai-action',
			title: formatMessage(i18n.pageCatchupMenuItemLabel),
			icon: () => <ClockIcon label="" testId="summarize-changes-ai-action.icon" />,
			viewConfig: {
				render: () => (
					<PageCatchupComponent
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
						allowCustomFollowUp
					/>
				),
				headerRightAlignedElement: fg('cc_ai_page_catchup_filtering') ? (
					<FilterComponent />
				) : undefined,
			},
			type: 'ai-action' as AIActionType,
			handleType: HandleActionType.Base,
			customFollowUpContext: {
				available: false,
				fetch: () => Promise.resolve([]),
			},
			nudgeContext: {
				id: 'content-catchup',
			},
		}),
		[formatMessage, contentId, contentType, spaceKey],
	);

	const quickSummaryAction = useMemo(
		() => ({
			id: 'summarize-page-ai-action',
			title: formatMessage(i18n.summarizeSectionLabel),
			icon: () => <ListBulletedIcon label="" testId="summarize-page-ai-action.icon" />,
			viewConfig: {
				render: () => (
					<QuickSummaryStreamingComponent
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						lastModifiedDate={lastModifiedDate ?? ''}
						source="view-page"
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
						allowCustomFollowUp
					/>
				),
			},
			type: 'ai-action' as AIActionType,
			handleType: HandleActionType.Base,
			customFollowUpContext: {
				available: false,
				fetch: () => Promise.resolve([]),
			},
			nudgeContext: {
				id: 'page-summaries',
			},
		}),
		[formatMessage, contentId, contentType, spaceKey, lastModifiedDate],
	);

	const onBulkIssueCreate = useCallback(() => {
		if (isSidePanelOpen) {
			showWarningDialog(WarningDialog.FLOATING);
		} else {
			void handleBulkIssueCreate();
		}
	}, [isSidePanelOpen, showWarningDialog, handleBulkIssueCreate]);

	const actions = useMemo(() => {
		const baseActions: ConversationAction[] = [quickSummaryAction, pageCatchupAction];

		// Only show comment summary in Rovo button when proactive_comment_summary experiment is disabled
		if (
			(unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0) &&
			!expValEquals('proactive_comment_summary', 'isEnabled', true)
		) {
			baseActions.push({
				id: 'summarize-comments-ai-action',
				title: formatMessage(i18n.inlineCommentsMenuItemLabel),
				icon: () => <CommentIcon label="" />,
				viewConfig: {
					render: () => (
						<CommentCountsProvider contentId={contentId}>
							<FooterCommentsSummaryCombined
								contentId={contentId}
								contentType={contentType}
								source={CommentsSummarySource.Footer}
								spaceKey={spaceKey}
								isInObjectSidebar
								showAIDisclaimer={false}
							/>
						</CommentCountsProvider>
					),
				},
				type: 'ai-action' as AIActionType,
				handleType: HandleActionType.Base,
			});
		}

		if (pageMode === PageMode.VIEW && fg('confluence_ai_bulk_issue_creation')) {
			baseActions.push({
				id: 'bulk-issue-create-ai-action',
				title: formatMessage(
					fg('confluence-issue-terminology-refresh')
						? i18n.bulkWorkItemsCreateMenuItemLabel
						: i18n.bulkIssueCreateMenuItemLabel,
				),
				icon: () => <WorkItemsIcon label="" />,
				onClick: onBulkIssueCreate,
				type: 'ai-action' as AIActionType,
				handleType: HandleActionType.Base,
			});
		}

		if (fg('platform-linking-confluence-teamwork-context-panel')) {
			baseActions.push({
				id: 'explore-related-work',
				title: formatMessage(i18n.exploreRelatedWorkAndUpdates),
				icon: () => <TeamworkGraphIcon label="" />,
				viewConfig: {
					render: () => (
						<ExploreRelatedWork
							contentId={contentId}
							contentTitle={contentTitle}
							showFooter={false}
						/>
					),
				},
				type: 'ai-action' as AIActionType,
				handleType: HandleActionType.Base,
			});
		}

		return baseActions;
	}, [
		formatMessage,
		contentId,
		contentTitle,
		spaceKey,
		contentType,
		unresolvedInlineCommentsCount,
		footerCommentsCount,
		quickSummaryAction,
		pageCatchupAction,
		onBulkIssueCreate,
		pageMode,
	]);

	useEffect(() => {
		const isPageOrBlogpost = contentType === 'page' || contentType === 'blogpost';
		setConsumptionActions(isPageOrBlogpost && !isUnpublishedDraft ? actions : []); // needed for space overview since it doesn't have content type

		if (fg('aix_nudge_recommendation_migration_enabled')) {
			if (enableNudgeCheck) {
				const allNudges: ConversationAction[] = [pageCatchupAction];
				if (expValEquals('rovo_awareness_experiment', 'cohort', 'test')) {
					allNudges.push(quickSummaryAction);
				}
				setNudgesRefreshed(allNudges);
			} else {
				setNudgesRefreshed([]);
			}
		} else {
			if (showSummarizeAsDefault && isPageOrBlogpost) {
				setNudgesRefreshed([
					{
						...quickSummaryAction,
						icon: () => <AiGenerativeTextSummaryIcon label="" />,
					},
				]);
			}

			if (showCatchupAsDefault && isPageOrBlogpost) {
				setNudgesRefreshed([
					{
						...pageCatchupAction,
						icon: () => <ClockIcon label="" />,
					},
				]);
			}
		}
	}, [
		actions,
		canManageAutomations,
		contentId,
		contentTitle,
		contentType,
		enableNudgeCheck,
		isUnpublishedDraft,
		pageCatchupAction,
		quickSummaryAction,
		setConsumptionActions,
		setNudgesRefreshed,
		showCatchupAsDefault,
		showSummarizeAsDefault,
		spaceKey,
	]);
};
